import { create } from 'zustand';

interface SidebarSections {
  tools: boolean;
  operations: boolean;
}

interface UIState {
  // State
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  sidebarSections: SidebarSections;
  isCommandPaletteOpen: boolean;
  dashWidgetsUnlocked: boolean;
  dashShowCatalog: boolean;
  showCreateProjectModal: boolean;
  showProjectSwitcher: boolean;
  /** #311: bij open van ProjectSwitcher direct de Prullenbak-tab activeren
   *  zodat de "Naar Prullenbak gaan"-knop in PrivacySettings de gebruiker
   *  meteen op de juiste plek aflevert. Wordt gereset zodra de switcher
   *  sluit. */
  projectSwitcherInitialTab: 'projects' | 'trash' | null;
  showWhatsNew: boolean;
  whatsNewData: { version: string; body: string } | null;
  showWelcomeBanner: boolean;
  dashStats: { persons: number; sources: number; places: number; stories: number } | null;

  // Actions
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  toggleSidebarCollapse: () => void;
  toggleSidebarSection: (section: keyof SidebarSections) => void;
  setIsCommandPaletteOpen: (open: boolean) => void;
  setDashWidgetsUnlocked: (val: boolean) => void;
  setDashShowCatalog: (val: boolean) => void;
  setShowCreateProjectModal: (val: boolean) => void;
  setShowProjectSwitcher: (val: boolean) => void;
  setProjectSwitcherInitialTab: (tab: 'projects' | 'trash' | null) => void;
  setShowWhatsNew: (val: boolean) => void;
  setWhatsNewData: (data: { version: string; body: string } | null) => void;
  setShowWelcomeBanner: (val: boolean) => void;
  setDashStats: (
    stats: { persons: number; sources: number; places: number; stories: number } | null
  ) => void;
}

const loadSidebarCollapsed = (): boolean => {
  try {
    return localStorage.getItem('pt-sidebar-collapsed') === 'true';
  } catch {
    return false;
  }
};

export const useUIStore = create<UIState>((set) => ({
  // Initial state
  sidebarOpen: false,
  sidebarCollapsed: loadSidebarCollapsed(),
  sidebarSections: { tools: false, operations: false },
  isCommandPaletteOpen: false,
  dashWidgetsUnlocked: false,
  dashShowCatalog: false,
  showCreateProjectModal: false,
  showProjectSwitcher: false,
  projectSwitcherInitialTab: null,
  showWhatsNew: false,
  whatsNewData: null,
  showWelcomeBanner: false,
  dashStats: null,

  // Actions
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  toggleSidebarCollapse: () =>
    set((state) => {
      const next = !state.sidebarCollapsed;
      try {
        localStorage.setItem('pt-sidebar-collapsed', String(next));
      } catch {
        /* ignore */
      }
      return { sidebarCollapsed: next };
    }),

  toggleSidebarSection: (section) =>
    set((state) => ({
      sidebarSections: {
        ...state.sidebarSections,
        [section]: !state.sidebarSections[section],
      },
    })),

  setIsCommandPaletteOpen: (isCommandPaletteOpen) => set({ isCommandPaletteOpen }),
  setDashWidgetsUnlocked: (dashWidgetsUnlocked) => set({ dashWidgetsUnlocked }),
  setDashShowCatalog: (dashShowCatalog) => set({ dashShowCatalog }),
  setShowCreateProjectModal: (showCreateProjectModal) => set({ showCreateProjectModal }),
  setShowProjectSwitcher: (showProjectSwitcher) => set({ showProjectSwitcher }),
  setProjectSwitcherInitialTab: (projectSwitcherInitialTab) => set({ projectSwitcherInitialTab }),
  setShowWhatsNew: (showWhatsNew) => set({ showWhatsNew }),
  setWhatsNewData: (whatsNewData) => set({ whatsNewData }),
  setShowWelcomeBanner: (showWelcomeBanner) => set({ showWelcomeBanner }),
  setDashStats: (dashStats) => set({ dashStats }),
}));
